import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Home, MapPin, MessageCircle, Plus } from 'lucide-react'
import { cn } from 'lib/utils'
import MobileBackButton from 'components/MobileBackButton/MobileBackButton'
import FieldAccountMenu from './FieldAccountMenu'
import { useChatPopup } from 'features/Chat'
import { FieldNotificationButton } from 'features/Notifications/components/FieldNotificationButton'
import { fieldRoutes } from 'constants/fieldRoutes'

export interface FieldShellProps {
	children: ReactNode
	/**
	 * Shown in the header. Kept short: there is very little width.
	 *
	 * A node rather than a string so the home can render the MYDealTeams mark
	 * instead of spelling the name out. It still renders inside the `h1`, so an
	 * image passed here supplies the heading's accessible name through its alt
	 * text and the screen keeps exactly one level-1 heading.
	 */
	title?: ReactNode
	/** Rendered at the right of the header, e.g. a close or settings control. */
	headerAction?: ReactNode
	/** Hide the bottom tabs on screens that own the whole viewport, like a map. */
	hideTabs?: boolean
}

/*
 * Only routes that exist.
 *
 * Quick Capture is a Phase 2 screen and /field/capture currently 404s, so its
 * tab was a dead end — and installed to the home screen there is no browser
 * back button, which makes a dead end a force-quit. A tab comes back when its
 * screen does.
 */
/**
 * The screens reached straight from Home.
 *
 * On these there is no level above to return to, so their control CLOSES back
 * to Home rather than stepping back. A detail screen, a location or a project,
 * keeps the arrow: from there back genuinely means up one level, to the list
 * that led there.
 */
const PRIMARY_ROUTES: string[] = [
	fieldRoutes.NEAR,
	fieldRoutes.NEW_SITE,
	fieldRoutes.TASKS,
	fieldRoutes.PROJECTS,
	fieldRoutes.PARTNERS,
	fieldRoutes.IMAGES,
]

const TABS = [
	{ to: fieldRoutes.HOME, label: 'Home', icon: Home, end: true },
	{ to: fieldRoutes.NEAR, label: 'Locations', icon: MapPin, end: false },
	{ to: fieldRoutes.NEW_SITE, label: 'New Site', icon: Plus, end: false },
]

/**
 * The field app's frame. Deliberately shares nothing with `ScreenShell`.
 *
 * **Sizing.** `100dvh`, not `100vh` and not `h-screen`. On iOS `100vh` reports
 * the full screen and ignores the browser's own chrome, so a bottom-anchored
 * control lands behind the toolbar — which cost three rounds of debugging on
 * the Locations map. `dvh` tracks the viewport as the chrome shows and hides,
 * which is what a fixed bottom bar needs.
 *
 * Installed to the home screen there is no chrome at all (verified on an iPhone
 * 14), so `dvh` and `vh` agree and the layout is simply correct. In a browser
 * tab it still has to be right, because not everyone will install it.
 *
 * **Safe areas.** The bottom bar pads by `env(safe-area-inset-bottom)` so it
 * clears the home indicator. That only resolves to a real value because
 * `viewport-fit=cover` is set in `index.html`; without it the inset is 0 and
 * this padding silently does nothing.
 *
 * **Navigation.** Installed, there is no browser back button. Every route
 * therefore has to be reachable from the tabs or from an in-screen control —
 * a screen with neither is a dead end with no way out but force-quitting.
 *
 * That rule caught chat: the popup is mounted app-wide and every way of
 * opening it lived in `HeaderCN`, which the field app deliberately does not
 * render. Chat was therefore unreachable in field mode — present, working, and
 * with no door. Hence the header button below.
 */
export function FieldShell({ children, title, headerAction, hideTabs = false }: FieldShellProps) {
	const chat = useChatPopup()
	const { pathname } = useLocation()
	// Home is the root of the field app, so there is nothing behind it.
	const isHome = pathname === fieldRoutes.HOME
	const isPrimary = PRIMARY_ROUTES.includes(pathname)

	return (
		<div className="flex flex-col h-[100dvh] overflow-hidden bg-gray-50">
			{(title || headerAction) && (
				<header
					className="flex items-center justify-between gap-3 px-4 border-b border-gray-200 bg-white shrink-0"
					style={{
						paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
						paddingBottom: '0.75rem',
					}}
				>
					{/* `min-w-0` so the title truncates rather than squeezing the
					    chat and back controls off the right edge. */}
					<h1 className="min-w-0 truncate text-lg font-semibold text-gray-900">{title}</h1>
					<div className="flex items-center gap-1 shrink-0">
						{headerAction}
						<FieldNotificationButton />
						<button
							type="button"
							onClick={() => chat.open()}
							aria-label="Messages"
							className="inline-flex items-center justify-center w-11 h-11 rounded-full text-gray-500"
						>
							<MessageCircle className="w-5 h-5" />
						</button>
						{/*
							The home screen gets the account menu; everywhere else gets
							Back.

							Home is the root of the field app, so there is nothing behind it
							and a back button there either does nothing or throws the user
							out. The avatar is the better use of the space, and it is where
							the desktop keeps the same menu.

							Elsewhere the control carries an ARROW rather than an X, because
							it navigates rather than dismisses, and it falls back to the
							field home rather than the desktop one: on a cold load the
							desktop default would drop the user out of the app entirely.
						*/}
						{isHome ? (
							<FieldAccountMenu />
						) : (
							<MobileBackButton
								fallbackTo={fieldRoutes.HOME}
								mode={isPrimary ? 'close' : 'back'}
							/>
						)}
					</div>
				</header>
			)}

			{/* The only scrolling region. Keeping scroll here rather than on the
			    page means the tab bar cannot drift off-screen. */}
			<main className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{children}</main>

			{!hideTabs && (
				<nav
					className="flex items-stretch border-t border-gray-200 bg-white shrink-0"
					style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
				>
					{TABS.map(({ to, label, icon: Icon, end }) => (
						<NavLink
							key={to}
							to={to}
							end={end}
							className={({ isActive }) =>
								cn(
									// 44px minimum touch target, per the mobile guidance.
									'flex-1 flex flex-col items-center justify-center gap-1 min-h-[3.25rem] text-[11px] font-medium transition-colors',
									isActive ? 'text-primary' : 'text-gray-500',
								)
							}
						>
							<Icon className="w-5 h-5" />
							{label}
						</NavLink>
					))}
				</nav>
			)}
		</div>
	)
}

export default FieldShell
